import React from 'react';
import {fromJS, Map} from 'immutable';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import track from 'react-tracking';
import {upperFirst} from 'lib/stringUtils';
import {callAction, callTargetedAction} from 'client/framework';
import {addToast} from '@albert-io/atomic';
import TextInput from 'client/generic/Forms/TextInput/TextInput.react';
import TextAreaInput from 'client/generic/FormInputs/TextAreaInput.react';
import Checkbox from 'client/generic/Forms/Checkbox/Checkbox.react';
import withTracking from 'lib/hocs/withTracking';

import createAssignmentStore from 'client/CreateAssignmentV2/CreateAssignment.store';
import createAssignmentActions from 'client/CreateAssignmentV2/CreateAssignment.actions';
import wizardActions from 'client/generic/Wizard/Wizard.actions';
import {ConfirmChangesModal} from 'client/generic/ConfirmChangesModal/ConfirmChangesModal.react';
import {confirmChangesModalActions} from 'client/generic/ConfirmChangesModal/ConfirmChangesModal.actions';
import IconTooltip from 'sg/IconTooltip/IconTooltip.react';

import {confirmChangesRules, debouncedHandleInputChange, handleInputChange} from '../shared/utils';

import CreateAssignmentSchedulingOptions from './CreateAssignmentSchedulingOptions/CreateAssignmentSchedulingOptions.react';
import CreateAssignmentDateTimeOptions from './CreateAssignmentDateTimeOptions/CreateAssignmentDateTimeOptions.react';
import CreateAssignmentExplanationOptions from './CreateAssignmentExplanationOptions/CreateAssignmentExplanationOptions.react';

import './create-assignment-options.scss';

const windowConfirmStoreName = 'CreateAssignmentWindowConfirmStore';
const confirmChangesModalStoreName = 'CreateAssignmentconfirmChangesModalStore';

const TrackedInput = withTracking(TextInput, {
  onFocus: {event: 'click', type: 'input', content: (e, props) => props.label}
});

const TrackedTextAreaInput = withTracking(TextAreaInput, {
  onFocus: {event: 'click', type: 'input', content: (e, props) => props.label}
});

const TrackedCreateAssignmentSchedulingOptions = withTracking(CreateAssignmentSchedulingOptions, {
  onFocus: {event: 'click', type: 'input', content: (e, props) => props.label}
});

const TrackedCreateAssignmentDateTimeOptions = withTracking(CreateAssignmentDateTimeOptions, {
  onFocus: {event: 'click', type: 'input', content: (e, props) => props.label}
});

const TrackedCheckbox = withTracking(Checkbox, {
  onChange: {event: 'click', type: 'checkbox', content: (e, props) => props.label}
});

const TrackedCheckboxWithTooltip = withTracking(Checkbox, {
  onChange: {event: 'click', type: 'checkbox', content: (e, props) => props.label.props.children[0]}
});

const TrackedIconTooltip = withTracking(IconTooltip, {
  onMouseEnter: {event: 'hover', type: 'tooltip', content: 'Randomize question order?'}
});

const TrackedCreateAssignmentExplanationOptions = withTracking(CreateAssignmentExplanationOptions, {
  onChange: {
    event: 'click',
    type: 'radio',
    content: (e) => e.target.value
  }
});

@track({component: 'Step 2: Assignment Options', feature: 'ACF'})
class CreateAssignmentOptions extends React.Component {
  static propTypes = {
    tracking: PropTypes.object
  };

  UNSAFE_componentWillMount() {
    if (createAssignmentStore.getIsEditMode()) {
      /*
        If the user didn't choose a start_date for an assignment,
        Mandark will create the assignment using its inserted_at value as
        the start_date. We don't want to confuse users by showing
        them this date in edit mode, so we check if the start_date is a
        bona fide start_date and clear the input ahead of time if necessary.
      */
      if (!createAssignmentStore.hasStartDate()) {
        callAction(
          createAssignmentActions.SET_FIELD_STATE,
          new Map({
            key: 'start_date',
            value: null
          })
        );
      }
      callAction(createAssignmentActions.VALIDATE_ASSIGNMENT);
    }
  }

  componentDidMount() {
    global.scrollTo(0, 0);
  }

  /*
    Assignment options should be **controlled inputs** handled exclusively via props.
    All values should be peeled off the store in this component and passed down in turn.
  */
  debouncedHandleChange = (e) => {
    debouncedHandleInputChange(e.target.name, e.target.value, e.target.type);
  };

  handleChange = (e) => {
    handleInputChange(e.target.name, e.target.value, e.target.type);
  };

  handleCheck = (e) => handleInputChange(e.target.name, e.target.checked);

  @track({type: 'button', content: 'Submit', event: 'click'})
  onSaveClick = () => {
    callTargetedAction({
      name: confirmChangesModalActions.TOGGLE_MODAL,
      targetStore: confirmChangesModalStoreName,
      payload: true
    });
  };

  handleSave = () => {
    callAction(createAssignmentActions.SYNC_ROSTERS);
    createAssignmentStore.getSyncRostersPromise().then(() => {
      callAction(createAssignmentActions.SAVE_ASSIGNMENT);

      // since the BE doesn't support bulk_updates, edits will need to be a list of promises and creates are standalone
      const resolvedPromise = createAssignmentStore.getIsEditMode()
        ? Promise.all(createAssignmentStore.getPromiseResponse())
        : createAssignmentStore.getPromiseResponse();

      resolvedPromise.then((assignments) => {
        callAction(createAssignmentActions.SET_SAVED_ASSIGNMENT_PAYLOAD, fromJS(assignments));
        this.handleResponse();
      });
    });
  };

  handleResponse() {
    const isSaveSuccessful = createAssignmentStore.getIsSaveSuccessful();
    const toastMessages = createAssignmentStore.getToastMessages();

    if (!isSaveSuccessful) {
      this.props.tracking.trackEvent({
        type: 'message',
        content: toastMessages.first(),
        event: 'error'
      });

      addToast({
        message:
          toastMessages.size > 1 ? (
            <ul>
              {toastMessages.map((message, key) => (
                <li key={key}>{message}</li>
              ))}
            </ul>
          ) : (
            <p>{toastMessages.first()}</p>
          ),
        title: 'Something went wrong',
        type: 'danger'
      });
    }

    callAction(createAssignmentActions.RESET_TOAST_MESSAGE);

    if (isSaveSuccessful) {
      callAction(wizardActions.ADVANCE_STEP);
    }
  }

  getConfirmationMessageList() {
    return (
      <div>
        <ul
          className={classnames('acf__confirm-changes', {
            'acf__confirm-changes--unbullet':
              createAssignmentStore.getConfirmationMessage().size === 1
          })}
        >
          {createAssignmentStore.getConfirmationMessage().map((message, i) => (
            <li key={i} className='acf__confirm-changes-msg'>
              {message}
            </li>
          ))}
        </ul>
        <p>Are you sure you want to continue?</p>
      </div>
    );
  }

  render() {
    const isAssigmentValid = createAssignmentStore.getIsAssignmentValid();
    const isSavePending = createAssignmentStore.getIsSavePending();
    const isSaveSuccessful = createAssignmentStore.getIsSaveSuccessful();
    const randomizeQuestionOrder = createAssignmentStore.isRandomizeQuestionOrder();
    const allowLate = createAssignmentStore.isAllowLateSubmissions();
    const correctAnswerSetting = createAssignmentStore.getCorrectAnswerSetting();
    const dueDate = createAssignmentStore.getDueDate();
    const errorData = createAssignmentStore.getErrorData();
    const message = createAssignmentStore.getMessage();
    const name = createAssignmentStore.getName();
    const startDate = createAssignmentStore.getStartDate();
    const timeLimit = createAssignmentStore.getTimeLimit();
    const assignmentTypeLabel = createAssignmentStore.getAssignmentTypeLabel();

    const randomizeQuestionOrderField = !createAssignmentStore.isAssigningPracticeExam() ? (
      <div className='acf__options-row'>
        <div className='acf__input-wrapper acf__checkbox-input'>
          <TrackedCheckboxWithTooltip
            checked={randomizeQuestionOrder}
            error={errorData.has('randomize_question_order')}
            errorMessage={errorData.get('randomize_question_order')}
            label={
              <span>
                Randomize question order per student
                <TrackedIconTooltip
                  className='acf__icon-tooltip'
                  color='dark'
                  content={
                    <div>
                      If enabled, questions will appear in a different order for each student while
                      the assignment is in progress. Questions will appear in their original order
                      after the assignment is submitted.{' '}
                      <b> Note: Related questions may be separated.</b>
                    </div>
                  }
                  icon='fa-question-circle'
                  position='top'
                  onHover
                  onMouseEnter={() => console.log('outer hover')}
                />
              </span>
            }
            name='randomize_question_order'
            onChange={this.handleCheck}
          />
        </div>
      </div>
    ) : null;

    return (
      <div>
        <div className='acf__options-wrapper'>
          <div className='acf__options-row'>
            <div className='acf__input-wrapper acf__input-wrapper--title'>
              <TrackedInput
                className='acf__input acf__input--required acf__input--title'
                data-testid='assignment_name'
                error={errorData.has('name')}
                errorMessage={errorData.get('name')}
                label={`${upperFirst(assignmentTypeLabel)} title`}
                name='name'
                placeholder='E.g. Biology Midterm'
                required
                type='text'
                value={name}
                onChange={this.debouncedHandleChange}
              />
            </div>
          </div>
          <TrackedCreateAssignmentSchedulingOptions
            errorData={errorData}
            onChange={this.debouncedHandleChange}
            startDate={startDate}
            onFocus={() => {}}
          />
          <TrackedCreateAssignmentDateTimeOptions
            dueDate={dueDate}
            errorData={errorData}
            onChange={this.debouncedHandleChange}
            timeLimit={timeLimit}
            sections={createAssignmentStore.getSections()}
          />
          <div className='acf__options-row'>
            <div className='acf__input-wrapper acf__checkbox-input'>
              <TrackedCheckbox
                checked={allowLate}
                error={errorData.has('allow_late_submissions')}
                errorMessage={errorData.get('allow_late_submissions')}
                label='Allow late submissions'
                name='allow_late_submissions'
                onChange={this.handleCheck}
              />
            </div>
          </div>
          {randomizeQuestionOrderField}
          <TrackedCreateAssignmentExplanationOptions
            correctAnswerSetting={correctAnswerSetting}
            onChange={this.handleChange}
            assignmentType={assignmentTypeLabel}
          />
          <div className='acf__options-row'>
            <TrackedTextAreaInput
              inputClasses='acf__input--textarea'
              label='Message (optional)'
              name='message'
              onChange={this.debouncedHandleChange}
              defaultValue={message}
            />
          </div>
        </div>
        <div className='acf__btn-wrapper'>
          <button
            className='acf__btn acf__btn--default acf__btn--cancel'
            onClick={() => {
              this.props.tracking.trackEvent({type: 'button', content: 'Back', event: 'click'});
              callAction(wizardActions.RETREAT_STEP);
            }}
            type='button'
          >
            Back
          </button>
          <button
            className='acf__btn acf__btn--success acf__btn--submit'
            disabled={!isAssigmentValid || isSavePending || isSaveSuccessful}
            onClick={this.onSaveClick}
            type='submit'
            data-testid='acf__submit-btn'
          >
            Submit
          </button>
        </div>
        <ConfirmChangesModal
          oldResource={createAssignmentStore.getOriginalAssignment()}
          onConfirm={this.handleSave}
          newResource={createAssignmentStore.getAssignmentModel()}
          rules={confirmChangesRules}
          storeName={confirmChangesModalStoreName}
          windowConfirmStoreName={windowConfirmStoreName}
        />
      </div>
    );
  }
}

export default CreateAssignmentOptions;
